import * as ImagePicker from 'expo-image-picker';
import { FILE_UPLOAD } from '@tabbio/utils';
import type { CreateImageUploadUrlInput } from '@tabbio/types';
import { createImageUploadUrl } from './media-api';

export interface UploadableImageAsset {
  uri: string;
  name: string;
  type: string;
  size?: number | null;
  file?: Blob;
}

export type ImageUploadErrorCode =
  | 'unsupported-type'
  | 'too-large'
  | 'read-failed'
  | 'upload-failed';

/**
 * Carries a machine-readable `code` so callers can show a specific, translated
 * message instead of a generic "try again". Falls back gracefully: callers that
 * only catch `Error` still get a human-readable `message`.
 */
export class ImageUploadError extends Error {
  readonly code: ImageUploadErrorCode;

  constructor(code: ImageUploadErrorCode, message: string) {
    super(message);
    this.name = 'ImageUploadError';
    this.code = code;
  }
}

export interface PickImageOptions {
  /** iOS/Android in-picker crop UI. Defaults to true. Pass false to keep the
   * image intact (e.g. a screenshot that must not be cropped to an aspect). */
  allowsEditing?: boolean;
  aspect?: [number, number];
}

const MAX_FILE_SIZE = FILE_UPLOAD.maxSizeMB * 1024 * 1024;

export async function pickImageFromLibrary(
  options: PickImageOptions = {},
): Promise<UploadableImageAsset | null> {
  const { allowsEditing = true, aspect = [4, 3] } = options;

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ['images'],
    allowsMultipleSelection: false,
    quality: 0.85,
    allowsEditing,
    ...(allowsEditing ? { aspect } : {}),
  });

  if (result.canceled || !result.assets?.[0]) {
    return null;
  }

  return fromImagePickerAsset(result.assets[0]);
}

export function fromImagePickerAsset(asset: {
  uri: string;
  fileName?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
}): UploadableImageAsset {
  return validateImageAsset({
    uri: asset.uri,
    name: asset.fileName || 'image.jpg',
    type: asset.mimeType || inferMimeType(asset.fileName) || 'image/jpeg',
    size: asset.fileSize,
  });
}

export function fromDocumentPickerAsset(asset: {
  uri: string;
  name: string;
  mimeType?: string | null;
  size?: number | null;
}): UploadableImageAsset {
  return validateImageAsset({
    uri: asset.uri,
    name: asset.name,
    type: asset.mimeType || inferMimeType(asset.name) || 'image/jpeg',
    size: asset.size,
  });
}

export function fromWebFile(file: File): UploadableImageAsset {
  const type = file.type || inferMimeType(file.name) || 'image/jpeg';
  const validated = validateImageAsset({
    uri: '',
    name: file.name,
    type,
    size: file.size,
    file,
  });

  return {
    ...validated,
    uri: URL.createObjectURL(file),
  };
}

export async function uploadImageAsset(
  asset: UploadableImageAsset,
  input: CreateImageUploadUrlInput,
) {
  const uploadTarget = await createImageUploadUrl({
    ...input,
    fileName: asset.name,
    mimeType: asset.type,
  });

  const body = asset.file ?? (await uriToBlob(asset.uri));
  const response = await fetch(uploadTarget.uploadUrl, {
    method: 'PUT',
    headers: buildUploadHeaders(asset.type, uploadTarget.uploadHeaders),
    body,
  });

  if (!response.ok) {
    throw new ImageUploadError('upload-failed', 'Image upload failed');
  }

  return uploadTarget.fileUrl;
}

function buildUploadHeaders(
  contentType: string,
  uploadHeaders: Record<string, string> = {},
) {
  return {
    ...uploadHeaders,
    'Content-Type': contentType,
  };
}

function validateImageAsset(asset: UploadableImageAsset) {
  if (!FILE_UPLOAD.allowedImageTypes.includes(asset.type as (typeof FILE_UPLOAD.allowedImageTypes)[number])) {
    throw new ImageUploadError(
      'unsupported-type',
      'Unsupported image type. Please use JPG, PNG, or WebP.',
    );
  }

  if (asset.size && asset.size > MAX_FILE_SIZE) {
    throw new ImageUploadError(
      'too-large',
      `Image must be ${FILE_UPLOAD.maxSizeMB}MB or smaller.`,
    );
  }

  return asset;
}

async function uriToBlob(uri: string) {
  const response = await fetch(uri);
  if (!response.ok) {
    throw new ImageUploadError('read-failed', 'Could not read the selected image');
  }

  return response.blob();
}

function inferMimeType(fileName?: string | null) {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    default:
      return null;
  }
}
